import { FormValues, ParsedFormValues, SlideEntry } from './types';
import { getAgeFromDoB, getYearOfBirth } from '../general/utils/date';
import { ConsultationResponse, ConsultationType, CustomDimensions, DoiStatus, IncomeKey, OccupationStatus, TrackingEvent } from '../types/consultation';

export function createFormStepEvent(formValues: ParsedFormValues, history: readonly SlideEntry[], isStart = false): TrackingEvent {
  const currentEntry = history[history.length - 1];
  const event = isStart ? 'mf_view_start' : currentEntry.order === 99 ? 'mf_view_result' : 'mf_view';
  const customDimensions = formValuesToCustomDimensions(formValues);
  const element_detail = inputFieldsToTrackingName(currentEntry.fields) || currentEntry.title;

  return {
    event,
    element_section: 'marketing-funnel-block',
    form_step: history.length,
    element_detail,
    ...customDimensions
  };
}

export function createRejectionEvent(formValues: ParsedFormValues, history: readonly SlideEntry[]): TrackingEvent {
  const { tracking } = history[history.length - 1];
  const customDimensions = formValuesToCustomDimensions(formValues);

  return {
    event: 'lead_rejected',
    element_section: 'marketing-funnel-block',
    rejection_reason: tracking.substring(4),
    ...customDimensions
  };
}
export async function createPhoneLeadEvent(formValues: ParsedFormValues, response: ConsultationResponse): Promise<TrackingEvent> {
  const { phone_number, email, first_name, last_name, consultation_language } = formValues;
  const leadsUserData = {
    email_address: email!,
    phone_number: phone_number || 'na',
    address: {
      first_name: first_name!,
      last_name: last_name!
    }
  };

  if (!formValues.hasOwnProperty('employment')) {
    formValues.employment = OccupationStatus.Undefined;
  }

  return {
    event: phone_number ? 'phone_lead' : 'email_lead',
    element_section: 'marketing-funnel-block',
    consultation_type: response.appointment_details ? ConsultationType.Appointment : ConsultationType.Callback,
    doi_status: getDoiStatus(response),
    leadsUserData,
    ...(phone_number && { lead_language: consultation_language || (en ? 'en' : 'de') }),
    ...formValuesToCustomDimensions(formValues)
  };
}

function formValuesToCustomDimensions(values: ParsedFormValues, implizit: (keyof FormValues)[] = []): CustomDimensions {
  const { date_of_birth, income, insurance_category, insurance_group, employment, visa, gender, nationality, jaeg_reached, customer_type } = values;

  return {
    ...(insurance_category && { insurance_category }),
    ...(insurance_group && { insurance_group }),
    ...(date_of_birth && {
      age: getAgeFromDoB(date_of_birth),
      yob: getYearOfBirth(date_of_birth)
    }),
    ...((income && incomeToDimensions(income)) || (jaeg_reached !== undefined && incomeToDimensions(jaeg_reached ? IncomeKey.More : IncomeKey.ThirtyToSixtyFive))),
    ...(jaeg_reached !== undefined && { jaeg_reached: jaeg_reached ? 1 : 0 }),
    ...(employment && { job_status: employment }),
    ...(nationality && { nationality: nationality.toLowerCase() }),
    ...(visa && { visa: visa.toLowerCase() }),
    ...(gender && { gender }),
    ...(customer_type && { customer_type })
  };
}

function incomeToDimensions(income: IncomeKey): CustomDimensions {
  if (income) {
    switch (income) {
      case 'more':
        return {
          income: '66601',
          jaeg_reached: 1
        };
      case 'less':
        return {
          income: '29999',
          jaeg_reached: 0
        };
      case '30to40':
      case '30to65':
      case '40to60':
        return {
          income: '66599',
          jaeg_reached: 0
        };
      default:
        return {
          income: 'na'
        };
    }
  } else return {};
}

function getDoiStatus({ appointment_details, consent_confirmed }: ConsultationResponse) {
  if (appointment_details && !appointment_details.doi_enabled) {
    return DoiStatus.Disabled;
  }

  if (appointment_details && appointment_details.status === 'confirmed') {
    return DoiStatus.Validated;
  }

  if (consent_confirmed) {
    return DoiStatus.Validated;
  }

  return DoiStatus.Pending;
}

function inputFieldsToTrackingName(inputs: Set<string>): string | null {
  const inputArray = Array.from(inputs.keys());

  const groups: string[] = [];

  if (inputArray.length === 0) {
    return null;
  }

  for (const [trackingGroupName, trackingGroupInputs] of inputToGroupMappingTupel) {
    if (inputArray.some(input => trackingGroupInputs.includes(input))) {
      groups.push(trackingGroupName);
    }
  }

  //fallback to input name for single input forms
  if (groups.length === 0) {
    if (inputArray.length === 1) {
      return inputArray[0];
    } else {
      return null;
    }
  }

  return groups.join('+');
}

// maps input names to their respective tracking names for the group
const inputToGroupMappingTupel: [string, string[]][] = [
  ['personal', ['first_name', 'last_name', 'gender']],
  ['insurance_category', ['interest', ' insurance_category']],
  ['job_status', ['employment', 'job_status']],
  ['date_of_birth', ['date_of_birth']],
  ['nationality', ['nationality']],
  ['visa', ['visa', 'visa_status']],
  ['contact', ['phone_number', 'email']],
  ['tariff', ['tariff']],
  ['missing_teeth', ['dental_scope']],
  ['resident_status', ['visa_status']],
  ['previous_insurance', ['previous_insurance']],
  ['dental-scope', ['missing_teeth']],
  ['appointment', ['date', 'time', 'consultation_language']]
];
